import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HypothesesMetricSource as MetricSource,
  HypothesesMetricSourceCreate as MetricSourceCreate,
  HypothesesMetricSourceDetail as MetricSourceDetail,
  HypothesesMetricSourceList as MetricSourceList,
  HypothesesMetricSourceRef as MetricSourceRef,
  HypothesesMetricSourceUpdate as MetricSourceUpdate,
  HypothesesSourceQuestion as SourceQuestion,
  HypothesesSourceQuestionAnswer as SourceQuestionAnswer,
  HypothesesSourceQuestionForAnswerer as SourceQuestionForAnswerer,
  HypothesesSourceQuestionSummary as SourceQuestionSummary,
  HypothesesSourceStatus as SourceStatus,
} from '../generated/api/types.gen.ts';
import {
  hypothesisSourceQuestionsAnswer,
  hypothesisSourceQuestionsAsk,
  hypothesisSourceQuestionsCloseQuestion,
  hypothesisSourceQuestionsDecide,
  hypothesisSourceQuestionsGetQuestion,
  hypothesisSourceQuestionsListQuestions,
  hypothesisSourcesArchive,
  hypothesisSourcesCreate,
  hypothesisSourcesGet,
  hypothesisSourcesList,
  hypothesisSourcesUpdate,
} from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { behandlingNoekkel } from './useHypoteseBehandling.ts';
import { hypoteseNoekkel, type Hypothesis } from './useHypotheses.ts';

export type { MetricSource, MetricSourceCreate, MetricSourceDetail, MetricSourceList, MetricSourceRef, MetricSourceUpdate, SourceQuestion, SourceQuestionAnswer, SourceQuestionForAnswerer, SourceQuestionSummary, SourceStatus };

/**
 * H3.3 — kildekatalogen («Kilder hos …») og «Spør eieren av kilden» (Kh15).
 *
 * Katalogen leses av alle i rommet (skjemaets panel og siden under Innstillinger); eier legger
 * inn og arkiverer, kildens eier retter sin egen. Spørsmålet stilles fra innsenderens eget utkast,
 * og kildens eier leser utkastet BARE gjennom spørsmålet, og bare mens det er åpent — derfor er
 * det sitt eget kall, aldri `GET /hypotheses/{id}`.
 *
 * Nøklene eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). De deler
 * prefikset `hypotheses`, så en overgang som friskere opp rommet, friskere også katalogen.
 */
export const kildeNoekkel = {
  liste: (filter: KildeFilter) => ['hypotheses', 'kilder', 'liste', filter] as const,
  alle: ['hypotheses', 'kilder'] as const,
  detalj: (id: string) => ['hypotheses', 'kilder', 'detalj', id] as const,
  tilMeg: ['hypotheses', 'sporsmaal', 'til-meg'] as const,
  sporsmaal: (id: string) => ['hypotheses', 'sporsmaal', id] as const,
};

/** Katalogens filtre: søket treffer navn og hva den måler. `arkiverte` bare på siden under Innstillinger. */
export interface KildeFilter {
  q?: string;
  arkiverte?: boolean;
}

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Katalogen. Lest av alle i rommet; tallet på spørsmål som venter, bare for eier. */
export function useKilder(filter: KildeFilter = {}, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: kildeNoekkel.liste(filter),
    enabled,
    queryFn: async (): Promise<MetricSourceList> => {
      const q = filter.q?.trim();
      const res = await hypothesisSourcesList({ client: klient, query: { ...(q ? { q } : {}), ...(filter.arkiverte ? { includeArchived: true } : {}) } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as MetricSourceList;
    },
  });
}

/** Én kilde med spørsmålene som venter — spørsmålene bare for eier og kildens eier, aldri utkastene. */
export function useKilde(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: kildeNoekkel.detalj(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<MetricSourceDetail> => {
      const res = await hypothesisSourcesGet({ client: klient, path: { sourceId: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as MetricSourceDetail;
    },
  });
}

function friskOppKatalogen(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: kildeNoekkel.alle });
}

/** «Legg til kilde» — bare eier (tjeneren nekter alle andre). */
export function useOpprettKilde() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: MetricSourceCreate): Promise<MetricSource> => {
      const res = await hypothesisSourcesCreate({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as MetricSource;
    },
    onSuccess: () => friskOppKatalogen(queryClient),
  });
}

/** «Endre» — eier alt, kildens eier alt unntatt eieren. */
export function useEndreKilde() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: MetricSourceUpdate }): Promise<MetricSource> => {
      const res = await hypothesisSourcesUpdate({ client: klient, path: { sourceId: id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as MetricSource;
    },
    onSuccess: () => friskOppKatalogen(queryClient),
  });
}

/** «Arkiver kilden …» — slettes aldri; spørsmålene som venter, lukkes. */
export function useArkiverKilde() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<MetricSource> => {
      const res = await hypothesisSourcesArchive({ client: klient, path: { sourceId: id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as MetricSource;
    },
    onSuccess: () => {
      friskOppKatalogen(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'sporsmaal'] });
    },
  });
}

/** Spørsmålene den innloggede skal svare på — radene i «Til deg». Uten utkast. */
export function useSporsmaalTilMeg(enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: kildeNoekkel.tilMeg,
    enabled,
    queryFn: async (): Promise<SourceQuestion[]> => {
      const res = await hypothesisSourceQuestionsListQuestions({ client: klient, query: { toMe: true } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SourceQuestion[];
    },
  });
}

/**
 * Ett spørsmål slik den som svarer ser det: med utkastet, bare mens spørsmålet er åpent (Kh15).
 * Ingen cache utover siden: et utkast hun ikke lenger skal se, blir ikke liggende i minnet.
 */
export function useSporsmaal(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: kildeNoekkel.sporsmaal(id ?? ''),
    enabled: id !== null,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    queryFn: async (): Promise<SourceQuestionForAnswerer> => {
      const res = await hypothesisSourceQuestionsGetQuestion({ client: klient, path: { questionId: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SourceQuestionForAnswerer;
    },
  });
}

/** Etter et spørsmål, et svar eller en lukking: utkastet (sourceQuestion), lista og tellerne. */
function friskOppSporsmaal(queryClient: ReturnType<typeof useQueryClient>, hypoteseId?: string) {
  void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'sporsmaal'] });
  void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.counts });
  void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.mine });
  friskOppKatalogen(queryClient);
  if (hypoteseId) void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.detalj(hypoteseId) });
}

/** «Send spørsmålet» fra innsenderens utkast. */
export function useSpoerEieren() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ hypoteseId, kildeId, sporsmaal }: { hypoteseId: string; kildeId: string; sporsmaal: string }): Promise<SourceQuestion> => {
      const res = await hypothesisSourceQuestionsAsk({ client: klient, path: { id: hypoteseId }, body: { metricSourceId: kildeId, question: sporsmaal } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SourceQuestion;
    },
    onSuccess: (q) => friskOppSporsmaal(queryClient, q.hypothesisId),
  });
}

/** «Send forslaget» — kildens eier svarer; tilgangen til utkastet slutter med svaret. */
export function useSvarPaaSporsmaal() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: SourceQuestionAnswer }): Promise<SourceQuestion> => {
      const res = await hypothesisSourceQuestionsAnswer({ client: klient, path: { questionId: id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SourceQuestion;
    },
    onSuccess: (q) => {
      queryClient.removeQueries({ queryKey: kildeNoekkel.sporsmaal(q.id) });
      friskOppSporsmaal(queryClient);
    },
  });
}

/** «Lukk uten forslag» (kildens eier) eller «Lukk spørsmålet» (innsenderen). */
export function useLukkSporsmaal() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<SourceQuestion> => {
      const res = await hypothesisSourceQuestionsCloseQuestion({ client: klient, path: { questionId: id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as SourceQuestion;
    },
    onSuccess: (q) => {
      queryClient.removeQueries({ queryKey: kildeNoekkel.sporsmaal(q.id) });
      friskOppSporsmaal(queryClient, q.hypothesisId);
    },
  });
}

/**
 * «Bruk forslaget» / «Behold mitt». Med forslaget er det en autolagring hos tjeneren (versjonen
 * øker), så svaret er hypotesen — skjemaet laster den, aldri sin egen gamle versjon.
 */
export function useVelgForslag() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, bruk, versjon }: { id: string; bruk: boolean; versjon: number }): Promise<Hypothesis> => {
      const res = await hypothesisSourceQuestionsDecide({ client: klient, path: { questionId: id }, body: { useSuggestion: bruk, baseVersion: versjon } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
      friskOppSporsmaal(queryClient);
    },
  });
}
